import clsx from 'clsx';
import React, { useEffect, useState } from 'react';

import { ProButton } from '@components/Button/variants';
import EmailLeadCapture from '@components/EmailLeadCapture';
import { useAppContext } from '@contexts/AppContext';
import useEventTracking from '@hooks/useEventTracking';
import usePaywallCTAs from '@hooks/usePaywallCTAs';
import useWindowSize from '@hooks/useWindowSize';
import { Theme } from '@interfaces/global';
import { subscribe_path } from '@routes';
import { TrackingLocation } from '@tracking';
import { WIDTH_SM } from '@utils/constants';
import { FormLocation } from '@utils/enums';

import { ORG_CHARTS_PRO_ONLY_MESSAGE } from './constants';
import { OrgChartsPaywallProps } from './interfaces';

const OrgChartsHeaderPaywall = ({ currentUser, shouldShowPaywall }: OrgChartsPaywallProps) => {
  const [isMobile, setIsMobile] = useState(false);
  const { width } = useWindowSize();
  const { offers } = useAppContext();
  const { Track, trackEvent } = useEventTracking({
    location: TrackingLocation.OrgChartHeaderPaywall,
  });

  const { paywallCTAText, paywallCTAPath, handleCTAClickTracking } = usePaywallCTAs({
    currentUser,
    offers,
    trackEvent,
  });

  useEffect(() => {
    setIsMobile(window.innerWidth < WIDTH_SM);
  }, [width]);

  return (
    <Track>
      <aside
        className="w-full bg-grayscale-charcoal dark:bg-[var(--light-grayscale-charcoal)]"
        data-testid="paywall"
      >
        <div
          className={clsx(
            'mx-auto flex w-full max-w-container-lg flex-col items-center justify-between py-32 md:items-center md:px-[14px] lg:flex-row',
            currentUser ? 'mb-20 gap-32 px-16 sm:gap-48 lg:gap-0' : 'mb-32 gap-24 px-32'
          )}
        >
          <div className="flex flex-col text-left md:max-w-[724px]">
            <h3
              className={clsx(
                'my-0 font-suisse-works text-headline-xs text-white sm:text-headline-md md:text-headline-alt-md',
                currentUser ? 'sm:text-center lg:text-left' : 'text-left'
              )}
            >
              {ORG_CHARTS_PRO_ONLY_MESSAGE}
            </h3>
          </div>
          <div className={clsx('flex w-full flex-col', isMobile ? 'max-w-full' : 'max-w-[420px]')}>
            {currentUser ? (
              <ProButton
                as="link"
                href={paywallCTAPath()}
                onClickCapture={handleCTAClickTracking}
                fullWidth
              >
                {paywallCTAText}
              </ProButton>
            ) : shouldShowPaywall ? (
              <EmailLeadCapture
                buttonClassName="w-full md:w-auto flex-2 lg:mt-28"
                buttonText="Subscribe"
                formLocation={FormLocation.OrgChartsLanding}
                redirectToOnSuccess={subscribe_path({ offer: offers?.subscribe ?? undefined })}
                oauthButtonsClassName="sm:mt-16"
                inputClassName="w-full lg:mt-28 dark:bg-[var(--light-grayscale-charcoal)]"
                signInOptionClass="text-left !opacity-100 *:!opacity-100"
                expandableOathButtonsClassName="!text-grayscale-beige"
                theme={Theme.Dark}
                includeSignInOption
                isPro
                isVertical
                isVerticalMobile
                withOauth
                withOnlyOauthExpandable
              />
            ) : (
              <></>
            )}
          </div>
        </div>
      </aside>
    </Track>
  );
};

export default OrgChartsHeaderPaywall;
